import { useTheme } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAreaRowHover } from "../hooks/useAreaRowHover";
import { useAutoFit } from "../hooks/useAutoFit";
import { useAutoRowProjection } from "../hooks/useAutoRowProjection";
import { useBenchmark, type BenchmarkState } from "../hooks/useBenchmark";
import { useCanvasInteraction } from "../hooks/useCanvasInteraction";
import { useCanvasPanning } from "../hooks/useCanvasPanning";
import { useCanvasResize } from "../hooks/useCanvasResize";
import { useCanvasTouchGestures } from "../hooks/useCanvasTouchGestures";
import { useFPSControl } from "../hooks/useFPSControl";
import { useHighlightZoom } from "../hooks/useHighlightZoom";
import { useMouseHandlers } from "../hooks/useMouseHandlers";
import { useTerminalOverviewStats, type TerminalOverviewStats } from "../hooks/useTerminalOverviewStats";
import { useRotation } from "../hooks/useRotation";
import { useSelectionHandlers } from "../hooks/useSelectionHandlers";
import { useStorageEntityLoader } from "../hooks/useStorageEntityLoader";
import { useThrottle } from "../hooks/useThrottle";
import { useViewTransform } from "../hooks/useViewTransform";
import { useWarehouseFilters } from "../hooks/useWarehouseFilters";
import type { BufferedCanvasCache } from "../renderers/core/BufferedCanvasCache";
import type { ReadyConfig, ViewState } from "../shared/types";
import type { Place, PortBoundary, RowBoundary, StorageEntityInfo } from "../types/warehouse.types";
import { getPlaceById, getPlacesByIds, getPlacesByRow, getRowId } from "../utils/placeUtils";
import { BenchmarkModal } from "./BenchmarkModal";
import { BottomPanelContainer } from "./BottomPanelContainer";
import ConfiguredCanvasRenderer from "./ConfiguredCanvasRenderer";
import { ControlPanel } from "./ControlPanel";
import { FPSCounter } from "./FPSCounter";
import { HoverTooltip } from "./HoverTooltip";
import { PlaceDetailModal } from "./PlaceDetailModal";
import { Row2DProjectionCanvas } from "./Row2DProjectionCanvas";
import { SidePanel } from "./SidePanel";
import { HelpButton } from "./HelpButton";
import { ViewInfoPanel } from "./ViewInfoPanel";

export interface SidePanelRenderProps {
  allPlaces: Place[];
  filteredPlaces: Map<string, Place>;
  selectedPlaces: Place[];
  allSelectedPorts: PortBoundary[];
  storageEntities: StorageEntityInfo[];
  terminalId: number;
  // Filters
  activeLevels: Set<number>;
  activeRows: Set<string>;
  toggleLevel: (level: number) => void;
  toggleRow: (row: string) => void;
  selectAllLevels: () => void;
  clearAllLevels: () => void;
  selectAllRows: () => void;
  clearAllRows: () => void;
  rowOptions: { name: string; label: string }[];
  allLevels: number[];
  terminalOverviewStats: TerminalOverviewStats;
  // Interaction & State
  setSelectedPlaceIds: (ids: Set<string>) => void;
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  zoomIn: () => void;
  zoomOut: () => void;
  autoFit: () => void;
  resetRotation: () => void;
  handleRotateLeftMouseDown: (e: React.MouseEvent) => void;
  handleRotateRightMouseDown: (e: React.MouseEvent) => void;
  handleRotateMouseUp: () => void;
  // Labels
  showAreaLabels: boolean;
  setShowAreaLabels: (v: boolean) => void;
  showRowLabels: boolean;
  setShowRowLabels: (v: boolean) => void;
  showPortLabels: boolean;
  setShowPortLabels: (v: boolean) => void;
  // Benchmark & FPS
  startBenchmark: () => void;
  benchmarkState: BenchmarkState;
  showFPS: boolean;
  toggleFPS: () => void;
  fps: number;
  handleSearch: (addresses: number[]) => void;
}

interface ViewBaseProps {
  readyConfig: ReadyConfig;
  terminalId: number;
  viewState: ViewState;
  setViewState: React.Dispatch<React.SetStateAction<ViewState>>;
  urlSelectedPlaceIds: string[];
  hasPersistedView?: boolean;
  extraFilter?: (place: Place) => boolean;
  placeModifier?: (place: Place) => Place;
  showSelectAll?: boolean;
  sidePanelWidth?: string;
  renderSidePanel: (props: SidePanelRenderProps) => React.ReactNode;
}

export const ViewBase: React.FC<ViewBaseProps> = ({
  readyConfig,
  terminalId,
  viewState,
  setViewState,
  urlSelectedPlaceIds,
  hasPersistedView,
  extraFilter,
  placeModifier,
  showSelectAll = true,
  sidePanelWidth,
  renderSidePanel,
}) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bufferedCacheRef = useRef<BufferedCanvasCache | null>(null);

  const [showAreaLabels, setShowAreaLabels] = useState(true);
  const [showRowLabels, setShowRowLabels] = useState(false);
  const [showPortLabels, setShowPortLabels] = useState(true);

  const [selectedRow, setSelectedRow] = useState<RowBoundary | null>(null);
  const [selectedPlaceIds, setSelectedPlaceIds] = useState<Set<string>>(new Set());
  const [selectedPortNames, setSelectedPortNames] = useState<Set<string>>(new Set());
  const [highlightedRows, setHighlightedRows] = useState<Set<string>>(new Set());

  const hasInitializedUrlRowsRef = useRef(false);
  const [modalPlace, setModalPlace] = useState<Place | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { canvasSize } = useCanvasResize({ containerRef });
  const { worldToScreen, zoomIn, zoomOut } = useViewTransform({
    viewState,
    setViewState,
    canvasSize,
    canvasRef,
  });

  const {
    places: placesRecord,
    areaBoundaries,
    rowBoundaries,
    rackBoundaries = [],
    portBoundaries,
    allLevels,
    allRows,
  } = readyConfig;

  const rowOptions = useMemo(
    () =>
      allRows.map((name) => {
        const match = rowBoundaries.find((r) => r.name === name);
        return { name, label: match?.label || name };
      }),
    [allRows, rowBoundaries]
  );

  const places = useMemo(() => {
    const entries = Object.entries(placesRecord).map(([placeId, place]) => [
      placeId,
      { ...place, place_id: placeId },
    ]);
    return new Map(entries as [string, Place][]);
  }, [placesRecord]);

  const allPlacesList = useMemo(() => Array.from(places.values()), [places]);

  const {
    activeLevels,
    activeRows,
    toggleLevel,
    toggleRow,
    selectAllLevels,
    clearAllLevels,
    selectAllRows,
    clearAllRows,
  } = useWarehouseFilters({ allLevels, allRows });

  const filteredPlaces = useMemo(() => {
    const newPlaces = new Map<string, Place>();
    for (const [placeId, place] of places.entries()) {
      const rowId = getRowId(place);
      if (!activeLevels.has(place.level)) continue;
      if (!activeRows.has(rowId)) continue;
      if (extraFilter && !extraFilter(place)) continue;

      let finalPlace = place;
      if (placeModifier) finalPlace = placeModifier(place);

      newPlaces.set(placeId, finalPlace);
    }
    return newPlaces;
  }, [places, activeLevels, activeRows, extraFilter, placeModifier]);

  const terminalOverviewStats = useTerminalOverviewStats(allPlacesList);

  const allSelectedPlaces = useMemo(
    () => getPlacesByIds(places, Array.from(selectedPlaceIds)),
    [selectedPlaceIds, places]
  );
  const { storageEntities } = useStorageEntityLoader(allSelectedPlaces);
  const allSelectedPorts = useMemo(
    () => portBoundaries.filter((port) => selectedPortNames.has(port.name)),
    [selectedPortNames, portBoundaries]
  );

  useEffect(() => {
    if (!hasInitializedUrlRowsRef.current && urlSelectedPlaceIds.length > 0 && places.size > 0) {
      const rowIds = new Set<string>();
      const resolvedPlaceIds = new Set<string>();
      urlSelectedPlaceIds.forEach((placeId) => {
        const place = getPlaceById(places, placeId);
        if (place) {
          rowIds.add(getRowId(place));
          resolvedPlaceIds.add(place.place_id);
        }
      });
      setHighlightedRows(rowIds);
      if (resolvedPlaceIds.size > 0) setSelectedPlaceIds(new Set(resolvedPlaceIds));
      hasInitializedUrlRowsRef.current = true;
    }
  }, [urlSelectedPlaceIds, places]);

  const { autoFit } = useAutoFit({
    boundaries: areaBoundaries,
    canvasSize,
    setViewState,
    autoRun: false,
  });
  useEffect(() => {
    if (readyConfig && urlSelectedPlaceIds.length === 0 && !hasPersistedView) autoFit();
  }, [readyConfig, autoFit, urlSelectedPlaceIds.length, hasPersistedView]);

  // TODO Rework into hook, simplify.
  const [searchedPlaceIds, setSearchedPlaceIds] = useState<string[]>([]);

  const handleSearch = useCallback((placeIds: number[]) => {
      // Resolve numeric IDs from backend to internal place IDs (map keys)
      const foundPlaceIds = new Set<string>();

      placeIds.forEach(numId => {
          for (const [id, p] of places.entries()) {
              if (p.id === numId) {
                  foundPlaceIds.add(id);
                  break;
              }
          }
      });

      const ids = Array.from(foundPlaceIds);
      setSearchedPlaceIds(ids);
      setSelectedPlaceIds(foundPlaceIds);

      const levelsToEnable = new Set<number>();
      const rowsToEnable = new Set<string>();

      ids.forEach((id) => {
        const place = places.get(id);
        if (place) {
          if (!activeLevels.has(place.level)) {
            levelsToEnable.add(place.level);
          }
          const rowId = getRowId(place);
          if (!activeRows.has(rowId)) {
            rowsToEnable.add(rowId);
          }
        }
      });

      levelsToEnable.forEach((l) => toggleLevel(l));
      rowsToEnable.forEach((r) => toggleRow(r));
    },
    [places, activeLevels, activeRows, toggleLevel, toggleRow]
  );

  // TODO Try to use selected functionality through context.
  const highlightIds = useMemo(
    () => (searchedPlaceIds.length > 0 ? searchedPlaceIds : urlSelectedPlaceIds),
    [searchedPlaceIds, urlSelectedPlaceIds]
  );

  // Use highlight zoom hook for URL-based place highlighting and search
  const { zoomToHighlightedPlaces } = useHighlightZoom({
    highlightedPlaceIds: highlightIds,
    places,
    viewState,
    setViewState,
    canvasSize,
  });

  // Effect to zoom when search results change
  useEffect(() => {
    if (searchedPlaceIds.length > 0 && places.size > 0) {
      zoomToHighlightedPlaces();
    }
  }, [searchedPlaceIds, places.size, zoomToHighlightedPlaces]);

  const hasZoomedRef = useRef(false);
  useEffect(() => {
    if (!hasZoomedRef.current && urlSelectedPlaceIds.length > 0 && places.size > 0) {
      zoomToHighlightedPlaces();
      hasZoomedRef.current = true;
    }
  }, [urlSelectedPlaceIds.length, places.size, zoomToHighlightedPlaces]);

  const { handlePlaceMultiSelect, handlePlaceSelect, handlePortMultiSelect, handlePortSelect } =
    useSelectionHandlers({ setSelectedPlaceIds, setSelectedPortNames });

  const handlePlaceDoubleClick = useCallback((place: Place) => {
    setModalPlace(place);
    setIsModalOpen(true);
  }, []);
  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setModalPlace(null);
  }, []);
  const handleSelectAllRowPlaces = useCallback(
    (row: RowBoundary) => {
      if (!showSelectAll) return;
      const placesInRow = getPlacesByRow(places, row.name, activeLevels);
      setSelectedPlaceIds(new Set(placesInRow.map((p) => p.place_id)));
    },
    [places, activeLevels, showSelectAll]
  );

  const handleRowSelect = useCallback(
    (row: RowBoundary | null) => {
      if (hasInitializedUrlRowsRef.current && selectedPlaceIds.size > 0) {
        setSelectedRow(row);
        hasInitializedUrlRowsRef.current = false;
        return;
      }
      setSelectedRow(row);
      if (row) setHighlightedRows(new Set([row.name]));
      else setHighlightedRows(new Set());
    },
    [selectedPlaceIds.size]
  );

  // Closing the projection also drops the place selection: it is what the operator means
  // by closing, and leaving a place selected would let useAutoRowProjection reopen the
  // projection on the next selection change.
  const handleCloseRowProjection = useCallback(() => {
    setSelectedPlaceIds(new Set());
    setSelectedRow(null);
    setHighlightedRows(new Set());
  }, []);

  useAutoRowProjection({
    selectedPlaceIds,
    places,
    rowBoundaries,
    setSelectedRow: handleRowSelect,
  });

  const {
    handleRotateLeftMouseDown,
    handleRotateRightMouseDown,
    handleRotateMouseUp,
    resetRotation,
  } = useRotation({ setViewState });
  const panning = useCanvasPanning({ canvasRef, viewState, setViewState });
  const touchGestures = useCanvasTouchGestures({ canvasRef, setViewState });
  const interaction = useCanvasInteraction({
    canvasRef,
    places: filteredPlaces,
    viewState,
    rowBoundaries,
    portBoundaries,
    onRowSelect: handleRowSelect,
    onPlaceSelect: handlePlaceSelect,
    onPlaceMultiSelect: handlePlaceMultiSelect,
    onPlaceDoubleClick: handlePlaceDoubleClick,
    onRowDoubleClick: handleSelectAllRowPlaces,
    onPortSelect: handlePortSelect,
    onPortMultiSelect: handlePortMultiSelect,
    didPan: () => panning.isDragging,
    isTouching: touchGestures.isTouching,
    bufferedCacheRef,
  });

  const { handleMouseDown, handleMouseMove, handleMouseUp, handleMouseLeave, handleDoubleClick } =
    useMouseHandlers({ panning, interaction });

  const { hoveredPort, hoveredArea, hoveredRow, hoveredPlace, mousePosition } = useAreaRowHover({
    canvasRef,
    portBoundaries,
    areaBoundaries,
    rowBoundaries,
    viewState,
    places: filteredPlaces,
    bufferedCacheRef,
  });

  const throttledHoveredArea = useThrottle(hoveredArea, 16);
  const throttledHoveredRow = useThrottle(hoveredRow, 16);
  const throttledHoveredPort = useThrottle(hoveredPort, 16);

  const { showFPS, toggleFPS, fps } = useFPSControl();
  const { startBenchmark, benchmarkState, benchmarkResults, resetBenchmark } = useBenchmark({
    viewState,
    setViewState,
    places,
    canvasSize,
    setSelectedPlaceIds,
    zoomIn,
    zoomOut,
    handleRotateLeftMouseDown,
    handleRotateRightMouseDown,
    handleRotateMouseUp,
  });

  const [benchmarkModalOpen, setBenchmarkModalOpen] = useState(false);
  useEffect(() => {
    if (benchmarkState.status === "completed") setBenchmarkModalOpen(true);
  }, [benchmarkState.status]);

  return (
    <div style={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
      <div
        ref={containerRef}
        style={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}
      >
        <canvas
          ref={canvasRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          onDoubleClick={handleDoubleClick}
          onTouchStart={touchGestures.handleTouchStart}
          onTouchMove={touchGestures.handleTouchMove}
          onTouchEnd={touchGestures.handleTouchEnd}
          onTouchCancel={touchGestures.handleTouchCancel}
          style={{
            cursor:
              panning.isDragging || touchGestures.isTouching
                ? "grabbing"
                : hoveredPlace || hoveredPort
                ? "pointer"
                : "crosshair",
            background: theme.palette.storageUi.backgroundPrimary,
            display: "block",
            touchAction: "none",
          }}
        />

        <ConfiguredCanvasRenderer
          canvasRef={canvasRef}
          canvasSize={canvasSize}
          places={filteredPlaces}
          areaBoundaries={areaBoundaries}
          rowBoundaries={rowBoundaries}
          portBoundaries={portBoundaries}
          viewState={viewState}
          activeLevels={activeLevels}
          activeRows={activeRows}
          selectedPlaceIds={selectedPlaceIds}
          worldToScreen={worldToScreen}
          hoveredArea={throttledHoveredArea}
          hoveredRow={throttledHoveredRow}
          showAreaLabels={showAreaLabels}
          showRowLabels={showRowLabels}
          showPortLabels={showPortLabels}
          selectedRows={highlightedRows}
          hoveredPort={throttledHoveredPort}
          selectedPortNames={selectedPortNames}
          bufferedCacheRef={bufferedCacheRef}
        />

        {selectedRow && (
          <Row2DProjectionCanvas
            selectedRow={selectedRow}
            places={filteredPlaces}
            activeLevels={activeLevels}
            rackBoundaries={rackBoundaries}
            onPlaceSelect={handlePlaceSelect}
            onPlaceMultiSelect={handlePlaceMultiSelect}
            onPlaceDoubleClick={handlePlaceDoubleClick}
            onClose={handleCloseRowProjection}
            onSelectAllRowPlaces={handleSelectAllRowPlaces}
            canvasWidth={canvasSize.width}
            selectedPlaceIds={selectedPlaceIds}
            showSelectAll={showSelectAll}
          />
        )}

        <BottomPanelContainer>
          <div style={{ pointerEvents: "auto" }}>
            <HelpButton>
              <ViewInfoPanel selectedPlaceIds={selectedPlaceIds} selectedRow={selectedRow} />
            </HelpButton>
          </div>
          <div style={{ pointerEvents: "auto" }}>
            <ControlPanel
              onZoomIn={zoomIn}
              onZoomOut={zoomOut}
              onAutoFit={autoFit}
              onRotateLeftMouseDown={handleRotateLeftMouseDown}
              onRotateRightMouseDown={handleRotateRightMouseDown}
              onRotateMouseUp={handleRotateMouseUp}
              onResetRotation={resetRotation}
            />
          </div>
        </BottomPanelContainer>

        <HoverTooltip
          hoveredPort={hoveredPort}
          hoveredArea={hoveredArea}
          hoveredRow={hoveredRow}
          hoveredPlace={hoveredPlace}
          mousePosition={mousePosition}
        />
        {showFPS && <FPSCounter fps={fps} />}
      </div>

      <SidePanel width={sidePanelWidth}>
        {renderSidePanel({
          allPlaces: allPlacesList,
          filteredPlaces,
          selectedPlaces: allSelectedPlaces,
          allSelectedPorts,
          storageEntities,
          terminalId,
          activeLevels,
          activeRows,
          toggleLevel,
          toggleRow,
          selectAllLevels,
          clearAllLevels,
          selectAllRows,
          clearAllRows,
          rowOptions,
          allLevels,
          terminalOverviewStats,
          setSelectedPlaceIds,
          viewState,
          setViewState,
          zoomIn,
          zoomOut,
          autoFit,
          resetRotation,
          handleRotateLeftMouseDown,
          handleRotateRightMouseDown,
          handleRotateMouseUp,
          showAreaLabels,
          setShowAreaLabels,
          showRowLabels,
          setShowRowLabels,
          showPortLabels,
          setShowPortLabels,
          startBenchmark,
          benchmarkState,
          showFPS,
          toggleFPS,
          fps,
          handleSearch
        })}
      </SidePanel>

      <PlaceDetailModal
        place={modalPlace}
        open={isModalOpen}
        onClose={handleModalClose}
        storageEntities={storageEntities}
      />
      <BenchmarkModal
        open={benchmarkModalOpen}
        onClose={() => {
          setBenchmarkModalOpen(false);
          resetBenchmark();
        }}
        results={benchmarkResults}
      />
    </div>
  );
};
